import useIntersectionObserver from '~/lib/hooks/useIntersectionObserver';
import { useGetCarePlanForNoteQuery } from '~/store/services/carePlan';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  NoteStatus,
  PatientNoteListItemType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { ChakraBox, useToastMessage } from '@equip.health/ui';
import Loading from '~/pages/loading';
import { PatientNoteItemCarePlanSection } from './PatientNoteItemCarePlanSection';
import { useEffect } from 'react';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { ErrorBoundary } from 'react-error-boundary';
import { CarePlanErrorFallback } from '~/components/maud/patients/profile/tabs/careplan/CarePlanErrorFallback';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  note: PatientNoteListItemType;
};
export const PatientNoteItemCarePlanContainer = ({ note }: Props) => {
  const { noteExternalId, noteTemplateRevision } = note;
  const { currentUser } = useGlobalContext();
  const {
    visibleRef: carePlanSectionRef,
    isVisible: isCarePlanSectionVisible,
  } = useIntersectionObserver();

  const { createToast } = useToastMessage();

  const { carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled } =
    useFlags<LDFlags>();

  const { noteTemplateRevisions, isRevisionDataLoading } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      noteTemplateRevision?.templateRevisionId,
    );

  const noteTemplate = noteTemplateRevisions?.find(
    (ntr) =>
      ntr.templateRevisionId === noteTemplateRevision?.templateRevisionId,
  )?.template;

  const isMedPsychTemplate =
    noteTemplate?.templateCode === 'FUP_MED_TEAM' ||
    noteTemplate?.templateCode === 'INTAKE_MED_TEAM';

  // Med/psych templates gate on the LD flag alone (DB flags not yet populated
  // for these templates); non-med templates keep the DB-flag rule.
  const shouldShowCarePlanSection = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!noteTemplate?.showCarePlan;

  const shouldShowGoals = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!noteTemplate?.showCarePlanGoals;

  const shouldShowInterventions = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!noteTemplate?.showCarePlanInterventions;

  const {
    data: carePlan,
    isLoading: isGetCarePlanForNoteLoading,
    error: getCarePlanForNoteError,
  } = useGetCarePlanForNoteQuery(
    {
      noteId: noteExternalId,
      providerRole: currentUser.providerTypeDetails.name,
    },
    { skip: !isCarePlanSectionVisible || !shouldShowCarePlanSection },
  );

  useEffect(() => {
    if (getCarePlanForNoteError) {
      createToast({
        id: 'patient-note-item-care-plan-error',
        text: `An error occurred fetching care plan data for note titled ${note.title}`,
        variant: 'critical',
      });
    }
  }, [getCarePlanForNoteError, createToast, note.title]);

  return (
    <ChakraBox
      ref={carePlanSectionRef}
      data-testid={`patient-note-item__${noteExternalId}__care-plan__container`}
    >
      {shouldShowCarePlanSection &&
        (isRevisionDataLoading || isGetCarePlanForNoteLoading) &&
        isCarePlanSectionVisible && <Loading />}

      {shouldShowCarePlanSection && carePlan && (
        <ErrorBoundary fallback={<CarePlanErrorFallback />}>
          <PatientNoteItemCarePlanSection
            carePlan={carePlan}
            isSigned={note.noteStatus === NoteStatus.SIGNED}
            showGoals={shouldShowGoals}
            showInterventions={shouldShowInterventions}
          />
        </ErrorBoundary>
      )}
    </ChakraBox>
  );
};
